import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  ClickAwayListener,
  Checkbox,
  FormControlLabel,
  Link,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { ArrowBack, ArrowForward, Search, Check, KeyboardArrowDown, CalendarToday, NavigateBefore, NavigateNext, ExpandMore, Close } from '@mui/icons-material';
import api from '../../utils/api';
import { isApplicationEditable } from '../../utils/applicationEditing';
import { toast } from 'react-toastify';
import Footer from '../../components/Footer/Footer';
import { useAuth } from '../../context/AuthContext';
import { buildUploadUrl, fetchFileBlobUrl, downloadTemplate } from '../../utils/fileAccess';
import { isValidKenyaIdOrPassport, isValidKenyaCompanyReg, KENYA_ID_HELPER, KENYA_REG_HELPER } from '../../utils/kenyaValidators';
import { countries } from '../../utils/countries';
import { isValidPhone, PHONE_HELPER } from '../../utils/phone';
import useSetupConfig from '../../hooks/useSetupConfig';
import { processFileForUpload, processFilesForUpload } from '../../utils/compressImage';
import { format, parse, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';

const steps = [
  {
    number: 1,
    title: 'Basic Information',
    description: 'Company and contact details'
  },
  {
    number: 2,
    title: 'Payment Details',
    description: 'Bank account and credit terms'
  },
  {
    number: 3,
    title: 'Entity Details',
    description: 'Documents and Certifications'
  },
  {
    number: 4,
    title: 'Declarations',
    description: 'Funds and Info Processing'
  },
  {
    number: 5,
    title: 'Review Application',
    description: 'Confirm your application is okay'
  }
];

const creditPeriods = ['30 Days', '45 Days', '60 Days', '90 Days', '120 Days'];

const contactRelationships = [
  'CEO',
  'CFO',
  'Director',
  'Managing Director',
  'Manager',
  'Company Secretary',
  'Owner',
  'Partner',
  'Authorized Representative',
  'Other',
];

const mapEntityTypeToDisplay = (value) => {
  const mapping = {
    private_company: 'Private/Public Company',
    public_company: 'Private/Public Company',
    partnership: 'Partnerships',
    foreign_company: 'Foreign Company',
    individual: 'Individual/Sole Proprietor',
    trust: 'Trust',
    other: 'Private/Public Company',
    'Public/Private Company': 'Private/Public Company',
    'Limited Company': 'Private/Public Company',
    'Public Limited Company': 'Private/Public Company',
    'Partnership': 'Partnerships',
    'Sole Proprietorship': 'Individual/Sole Proprietor'
  };

  return mapping[value] || value || '';
};

const mapEntityTypeToLegalNature = (value) => {
  const displayValue = mapEntityTypeToDisplay(value);
  const mapping = {
    'Private/Public Company': 'company',
    'Partnerships': 'partnership',
    'Foreign Company': 'foreign_company',
    'Individual/Sole Proprietor': 'individual',
    'Trust': 'trust'
  };

  return mapping[displayValue] || 'company';
};

// Legacy source-of-wealth codes → display names (for applications saved before setup migration)
const legacyWealthMap = {
  salary: 'Salary',
  business_income: 'Business Income',
  investment: 'Investment',
  inheritance: 'Inheritance',
  loan: 'Loan',
  other: 'Other',
};

// Document fields that no entity type asks for any more, kept only so a draft
// saved before the policy rework still loads, renders and saves what it already
// holds. Nothing here is required, and nothing new should be added — the
// checklist lives in server/utils/documentRequirements.js and reaches this file
// through the entity_types setup config.
const LEGACY_DOC_FIELDS = {
  partnersTaxCompliance: 'partner_tax_compliance',
  beneficiariesIds: 'beneficiary_id',
};

// Array-valued legacy fields, so a saved draft's list is not flattened to a
// string on the way back out.
const LEGACY_MULTI_FIELDS = new Set(['beneficiariesIds']);

const SupplierApplication = () => {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id; // Works for both /application/:id/edit and /application/:id
  const theme = useTheme();
  const { user } = useAuth();

  // Setup-driven dropdowns
  const { items: entityTypeItems, names: entityTypes } = useSetupConfig('entity_types');
  const { items: currencyItems } = useSetupConfig('currencies');
  const { names: serviceTypeOptions } = useSetupConfig('service_types');
  const { names: wealthSourceOptions } = useSetupConfig('wealth_sources');
  const { names: bankNameOptions } = useSetupConfig('bank_names');

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Basic Information
    supplierName: '',
    registeredCountry: '',
    companyRegistrationNumber: '',
    companyEmail: '',
    companyWebsite: '',
    legalNature: '',
    physicalAddress: '',
    postalAddress: '',

    // Contact Person
    contactFullName: '',
    contactRelationship: '',
    contactIdPassport: '',
    contactPhone: '',
    contactEmail: '',

    // Payment Details
    bankName: '',
    bankNameOther: '',
    accountNumber: '',
    branch: '',
    currency: '',
    creditPeriod: '',

    // Entity Details
    entityType: '',
    serviceTypes: '',
    serviceTypesOther: '',
    servicesDescription: '',
    // File uploads
    businessPermit: null,
    certificateOfIncorporation: null,
    kraPinCertificate: null,
    etimsProof: null,
    financialStatements: null,
    cr12: null,
    companyProfile: null,
    bankReferenceLetter: null,
    directorsIds: [],
    // Partnership
    partnershipDeed: null,
    partnersPinCertificate: null,
    partnersTaxCompliance: null,
    partnerIds: [],
    // Foreign Company
    shareCertificate: null,
    registryExtract: null,
    taxComplianceCertificate: null,
    directorsNationalIds: [],
    directorsPassports: [],
    // Individual / Sole Proprietor
    nationalId: null,
    passportDocument: null,
    workPermit: null,
    policeClearance: null,
    resume: null,
    // Trust
    trustDeed: null,
    founderPin: null,
    foundersIds: [],
    beneficiariesIds: [],
    practicingCertificates: [],
    keyMembersResumes: [],

    // Declarations
    sourceOfWealth: '',
    sourceOfWealthOther: '',
    declarantFullName: '',
    declarantCapacity: '',
    declarantIdPassport: '',
    declarationDate: '',
    consentToProcessing: false,
    confirmInformationAccurate: false,
    ndaDocument: null,
  });

  // Documents required for the currently selected entity type (from setup config)
  //
  // A document an admin has switched off in Setups is dropped here rather than
  // at each use, mirroring requirementsFor() on the server — both the rendered
  // checklist and the pre-submit check below read this one list, so neither can
  // go on asking for something the server has stopped enforcing.
  const entityTypeDocs = useMemo(() => {
    if (!formData.entityType || entityTypeItems.length === 0) return [];
    const found = entityTypeItems.find((item) => item.name === formData.entityType);
    return (found?.documents || []).filter((d) => d.enabled !== false);
  }, [formData.entityType, entityTypeItems]);

  // Information requirements for the current entity type, from the same config
  // the server enforces. Empty until the config loads, and empty for a database
  // reconcile has not yet touched — both fall back to the hard-coded rules below.
  const entityTypeFields = useMemo(() => {
    if (!formData.entityType || entityTypeItems.length === 0) return [];
    const found = entityTypeItems.find((item) => item.name === formData.entityType);
    return found?.fields || [];
  }, [formData.entityType, entityTypeItems]);

  const fieldStates = useMemo(
    () => Object.fromEntries(entityTypeFields.map((f) => [f.field, f])),
    [entityTypeFields]
  );

  // A field with no configuration behaves exactly as it did before this feature.
  const fieldState = (name) => fieldStates[name]?.state;

  // Mirrors dependsOn handling in missingFields() (server/utils/documentRequirements.js):
  // a field whose parent does not match its configured value is not asked for at
  // all, whatever its own state says. No configured field declares a dependsOn
  // today, so this is dormant, but stays in lockstep with the server so it keeps
  // being true if one ever does.
  const dependsOnUnmet = (name) => {
    const parent = fieldStates[name]?.dependsOn;
    return Boolean(parent?.field) &&
      String(formData[parent.field] ?? '').trim() !== String(parent.equals ?? '').trim();
  };

  // Evaluated dependsOn-first, exactly like the server: a field is "hidden" from
  // both validation and rendering either because it is explicitly configured
  // hidden, or because its dependsOn parent doesn't match — the same one check
  // covers every call site below (checkConfigured and every render gate), so
  // there is only one place this can drift from the server.
  const isHidden = (name) => dependsOnUnmet(name) || fieldState(name) === 'hidden';
  // The state each field had before requirements became configurable. Consulted
  // only when a field has no configuration at all — while the config loads, or
  // on a database that has not been reconciled — so an unconfigured wizard
  // behaves exactly as it did before this feature. Company website was
  // deliberately never validated: plenty of legitimate suppliers do not have one.
  const LEGACY_STATES = { companyWebsite: 'optional' };
  const isRequiredField = (name) => {
    const state = fieldState(name);
    return state === undefined
      ? (LEGACY_STATES[name] || 'required') === 'required'
      : state === 'required';
  };

  // The format each field was checked against before requirements became
  // configurable. Used ONLY when the field has no configuration at all — while
  // the config is still loading, or before an entity type is chosen — so an
  // unconfigured wizard validates exactly as it did before this feature.
  //
  // A field that IS configured takes its config's `format`, even when that is
  // undefined: Foreign Company deliberately carries no format on
  // companyRegistrationNumber, and falling back to the Kenyan rule there would
  // reject the very registration numbers this feature exists to accept.
  const LEGACY_FORMATS = {
    companyRegistrationNumber: 'kenyaCompanyReg',
    declarantIdPassport: 'kenyaIdOrPassport',
  };
  const formatFor = (name) =>
    (fieldStates[name] ? fieldStates[name].format : LEGACY_FORMATS[name]);

  // Matches CHECKBOX_FIELDS in server/utils/documentRequirements.js. Keyed by
  // field name, not by the value's runtime type: these two are not declared on
  // the Supplier schema, so a value loaded from a stored record is never
  // coerced, and a string 'false' must not read as consent.
  const CHECKBOX_FIELDS = new Set(['consentToProcessing', 'confirmInformationAccurate']);

  // Every document field ANY entity type can ask for, plus the legacy ones.
  //
  // Deliberately the union rather than the current entity type's list. Upload,
  // save and load all run over this: a draft started as a partnership and
  // switched to a company still carries the partnership's filenames on the
  // record (Supplier is `strict: false`), and narrowing these passes to the
  // current checklist would quietly drop them on the next save.
  //
  // Validation is the one thing that uses the current entity type only — see
  // validateRequiredDocuments.
  const allDocFields = useMemo(() => {
    const byField = new Map();
    for (const [field, documentType] of Object.entries(LEGACY_DOC_FIELDS)) {
      byField.set(field, {
        field,
        documentType,
        uploadType: LEGACY_MULTI_FIELDS.has(field) ? 'multiple' : 'single',
      });
    }
    for (const item of entityTypeItems) {
      for (const doc of item.documents || []) {
        byField.set(doc.field, {
          field: doc.field,
          documentType: doc.documentType || 'other',
          uploadType: doc.uploadType === 'multiple' ? 'multiple' : 'single',
        });
      }
    }
    return [...byField.values()];
  }, [entityTypeItems]);

  const singleDocFields = useMemo(
    () => allDocFields.filter((d) => d.uploadType !== 'multiple').map((d) => d.field),
    [allDocFields]
  );
  const multiDocFields = useMemo(
    () => allDocFields.filter((d) => d.uploadType === 'multiple').map((d) => d.field),
    [allDocFields]
  );
  const docTypeByField = useMemo(
    () => Object.fromEntries(allDocFields.map((d) => [d.field, d.documentType])),
    [allDocFields]
  );

  const [loading, setLoading] = useState(false);
  const [countrySearchOpen, setCountrySearchOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [, setCountryAnchorEl] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [, setDatePickerAnchorEl] = useState(null);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [fileViewerUrl, setFileViewerUrl] = useState(null);
  const [fileViewerName, setFileViewerName] = useState('');

  // Match the code as well as the name: with the full ISO list behind this
  // field, typing "KE" is the fastest way to reach Kenya.
  const filteredCountries = useMemo(() => {
    const term = countrySearchTerm.trim().toLowerCase();
    if (!term) return countries;
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(term) ||
        country.code.toLowerCase() === term
    );
  }, [countrySearchTerm]);

  const selectedCountry = countries.find(c => c.name === formData.registeredCountry);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [expandedAccordion, setExpandedAccordion] = useState('basicInformation');

  const [fieldErrors, setFieldErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const validateInputField = (field, value) => {
    if (field === 'companyEmail' || field === 'contactEmail') {
      if (value && !emailRegex.test(value.trim())) return 'Please enter a valid email address';
    }
    if (field === 'contactPhone') {
      // Same rule the profile page uses, so a number accepted here is still
      // accepted when the supplier later edits their contact details. The loose
      // regex this replaced let through shapes no country actually dials.
      if (value && !isValidPhone(value)) return `Please enter a valid phone number. ${PHONE_HELPER}`;
    }
    return '';
  };

  const handleFieldBlur = (field) => {
    const err = validateInputField(field, formData[field]);
    setFieldErrors(prev => ({ ...prev, [field]: err }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Debug file uploads
    if (value instanceof File || (Array.isArray(value) && value[0] instanceof File)) {
      console.log(`File uploaded for ${field}:`, value instanceof File ? value.name : value.map(f => f.name));
    }
  };

  const handleViewFile = async (file, fileName = '') => {
    if (!file) return;

    let fileUrl = null;
    let displayName = fileName || 'File';

    if (file instanceof File) {
      fileUrl = URL.createObjectURL(file);
      displayName = file.name;
    } else if (typeof file === 'string') {
      if (file.startsWith('http://') || file.startsWith('https://')) {
        // A stored absolute URL pointing at our own old /uploads path still
        // names a supplier directory and a file; resolve it to the
        // authenticated /files route rather than fetching it directly.
        fileUrl = buildUploadUrl(file, applicationId || id) || file;
      } else {
        fileUrl = buildUploadUrl(file, applicationId || id);
      }
      displayName = fileName || file.split('/').pop() || 'File';
    }

    if (fileUrl) {
      if (!fileUrl.startsWith('blob:')) {
        try {
          fileUrl = await fetchFileBlobUrl(fileUrl);
        } catch (error) {
          toast.error(error.message || 'Failed to open file');
          return;
        }
      }
      setFileViewerUrl(fileUrl);
      setFileViewerName(displayName);
      setFileViewerOpen(true);
    }
  };

  // The form is behind `protect`, so it has to be fetched with the bearer
  // token and saved from a blob; a link straight to the endpoint 401s.
  const handleDownloadTemplate = async (key, entry) => {
    try {
      await downloadTemplate(key, entry?.downloadAs);
    } catch (error) {
      toast.error(error.message || 'Failed to download the form');
    }
  };

  const handleCloseFileViewer = () => {
    if (fileViewerUrl && fileViewerUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fileViewerUrl);
    }
    setFileViewerOpen(false);
    setFileViewerUrl(null);
    setFileViewerName('');
  };

  // Map form field names to document types for upload
  const getDocumentType = (fieldName) => docTypeByField[fieldName] || 'other';


  const renderSingleFileUpload = (fieldKey, label, helperText = 'Accepted: PDF, Word, Excel, Images (Max 20MB — images auto-resized if larger)', template = null) => {
    const templateEntry = template ? availableTemplates[template] : null;
    const value = formData[fieldKey];
    const displayValue = value
      ? (value instanceof File
        ? value.name
        : typeof value === 'string'
          ? value
          : 'File selected')
      : 'Choose file';

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="body2"
          sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
        >
          {label}
        </Typography>
        {templateEntry && (
          <Button
            variant="text"
            size="small"
            onClick={() => handleDownloadTemplate(template, templateEntry)}
            sx={{
              mb: 1,
              p: 0,
              textTransform: 'none',
              fontSize: '13px',
              color: '#578A18',
              justifyContent: 'flex-start',
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
            }}
          >
            Download the {templateEntry.label} form, complete it, then upload it below
          </Button>
        )}
        <Button
          component="label"
          variant="outlined"
          fullWidth
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            borderColor: '#d1d5db',
            color: '#6b7280',
            fontSize: '14px',
            py: 0.75,
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb'
            }
          }}
        >
          {displayValue}
          <input
            type="file"
            hidden
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
            onChange={async (e) => {
              if (e.target.files && e.target.files[0]) {
                try {
                  const file = await processFileForUpload(e.target.files[0]);
                  handleChange(fieldKey, file);
                } catch (err) {
                  toast.error(err.message);
                }
              }
            }}
            onClick={(e) => {
              e.target.value = '';
            }}
          />
        </Button>
        <Typography
          variant="caption"
          sx={{ color: '#9ca3af', fontSize: '11px', mt: 0.5, display: 'block' }}
        >
          {helperText}
        </Typography>
        {rejectedDocs[fieldKey] && !(value instanceof File) && (
          <Typography
            variant="caption"
            sx={{ color: '#b91c1c', fontSize: '12px', mt: 0.5, display: 'block', fontWeight: 500 }}
          >
            Replace this document — {rejectedDocs[fieldKey]}
          </Typography>
        )}
        {!formData[fieldKey] && (
          <Typography
            variant="caption"
            sx={{ color: '#9ca3af', fontSize: '12px', mt: 0.5, display: 'block' }}
          >
            No file chosen
          </Typography>
        )}
      </Box>
    );
  };

  const renderMultiFileUpload = (fieldKey, label, maxFiles = 10) => {
    const currentValue = formData[fieldKey];
    const count = Array.isArray(currentValue) ? currentValue.length : 0;
    const displayValue = count > 0 ? `${count} file${count > 1 ? 's' : ''} selected` : 'Choose files';

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="body2"
          sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
        >
          {label}
        </Typography>
        <Button
          component="label"
          variant="outlined"
          fullWidth
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            borderColor: '#d1d5db',
            color: count > 0 ? theme.palette.green.main : '#6b7280',
            fontSize: '14px',
            py: 0.75,
            fontWeight: count > 0 ? 500 : 400,
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb'
            }
          }}
        >
          {displayValue}
          <input
            type="file"
            multiple
            hidden
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
            onChange={async (e) => {
              if (e.target.files && e.target.files.length > 0) {
                let selected = Array.from(e.target.files);
                if (selected.length > maxFiles) {
                  toast.warning(`Only the first ${maxFiles} files will be uploaded. ${selected.length - maxFiles} file(s) ignored.`);
                  selected = selected.slice(0, maxFiles);
                }
                const { processed, errors } = await processFilesForUpload(selected);
                errors.forEach(msg => toast.error(msg));
                if (processed.length > 0) handleChange(fieldKey, processed);
              }
            }}
            onClick={(e) => {
              e.target.value = '';
            }}
          />
        </Button>
        <Typography
          variant="caption"
          sx={{ color: '#9ca3af', fontSize: '11px', mt: 0.5, display: 'block' }}
        >
          Accepted: PDF, Word, Excel, Images · Up to {maxFiles} files (Max 20MB each)
        </Typography>
        {rejectedDocs[fieldKey] && count === 0 && (
          <Typography
            variant="caption"
            sx={{ color: '#b91c1c', fontSize: '12px', mt: 0.5, display: 'block', fontWeight: 500 }}
          >
            Replace this document — {rejectedDocs[fieldKey]}
          </Typography>
        )}
        {count === 0 && (
          <Typography
            variant="caption"
            sx={{ color: '#9ca3af', fontSize: '12px', mt: 0.5, display: 'block' }}
          >
            No file chosen
          </Typography>
        )}
      </Box>
    );
  };

  // The checklist for the current entity type, exactly as configured.
  //
  // There used to be a hard-coded UNIVERSAL_DOCS list prepended here and forced
  // required regardless of config, which is how every foreign company, sole
  // proprietor and trust came to be asked for a business licence policy does not
  // require of them.
  const getRequiredDocuments = () =>
    entityTypeDocs.map((d) => ({
      type: d.uploadType === 'multiple' ? 'multi' : 'single',
      field: d.field,
      label: d.label,
      required: d.required !== false,
      group: d.group,
      template: d.template,
    }));

  // Render dynamic document
  const renderDocument = (doc) => {
    const label = doc.required ? `${doc.label} *` : `${doc.label} (optional)`;
    if (doc.type === 'single') {
      return renderSingleFileUpload(doc.field, label, undefined, doc.template);
    } else if (doc.type === 'multi') {
      return renderMultiFileUpload(doc.field, label);
    }
    return null;
  };

  // Upload a single file
  const uploadFile = async (file, fieldName, supplierId) => {
    if (!file || !(file instanceof File)) {
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('supplierId', supplierId);
      formData.append('documentType', getDocumentType(fieldName));
      formData.append('document', file);

      const response = await api.post('/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.data) {
        // Return the filename from the uploaded document
        return response.data.data.fileName;
      }
      return null;
    } catch (error) {
      console.error(`Error uploading ${fieldName}:`, error);
      const serverMessage = error.response?.data?.message;
      toast.error(serverMessage || `Failed to upload ${fieldName}`);
      return null;
    }
  };

  // Upload all files in the form
  const uploadAllFiles = async (supplierId) => {
    const updatedFormData = { ...formData };

    // Upload single file fields
    const singleFileFields = singleDocFields;

    for (const field of singleFileFields) {
      if (formData[field] instanceof File) {
        const uploadedFileName = await uploadFile(formData[field], field, supplierId);
        if (uploadedFileName) {
          updatedFormData[field] = uploadedFileName;
        }
      }
    }

    // Upload array file fields
    const arrayFileFields = multiDocFields;
    for (const field of arrayFileFields) {
      if (Array.isArray(formData[field]) && formData[field].length > 0) {
        const uploadedFiles = [];
        for (const file of formData[field]) {
          if (file instanceof File) {
            const uploadedFileName = await uploadFile(file, field, supplierId);
            if (uploadedFileName) {
              uploadedFiles.push(uploadedFileName);
            }
          } else if (typeof file === 'string') {
            // Keep existing filenames
            uploadedFiles.push(file);
          }
        }
        updatedFormData[field] = uploadedFiles;
      }
    }

    return updatedFormData;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parse(dateString, 'yyyy-MM-dd', new Date());
      return format(date, 'dd / MM / yyyy');
    } catch {
      return dateString;
    }
  };

  const handleDateClick = (e) => {
    setDatePickerAnchorEl(e.currentTarget);
    setDatePickerOpen(true);
  };

  const handleDateSelect = (date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    handleChange('declarationDate', formattedDate);
    setCurrentMonth(date);
    setDatePickerOpen(false);
  };

  const calendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const [applicationId, setApplicationId] = useState(id || null);

  // Load existing application data if ID is provided.
  //
  // Waits for the entity-type config. The file fields below are mapped from
  // allDocFields, which is empty until that config arrives — loading first would
  // map every upload to null, blank the form, and then persist those nulls over
  // the supplier's real filenames on the next save.
  useEffect(() => {
    if (id && entityTypeItems.length > 0) {
      const loadApplication = async () => {
        try {
          const response = await api.get(`/suppliers/${id}`);
          const app = response.data.data;
          // Under review or complete: nothing here may change, so show the
          // read-only view instead of a form whose saves the server refuses.
          if (app && !isApplicationEditable(app.status)) {
            navigate(`/application/${id}`, { replace: true });
            return;
          }
          if (app) {
            // Debug: log the app data to see what fields are available
            console.log('Loaded application data:', app);
            console.log('Available fields:', Object.keys(app));
            console.log('File fields from backend (raw):', {
              certificateOfIncorporation: app.certificateOfIncorporation,
              kraPinCertificate: app.kraPinCertificate,
              etimsProof: app.etimsProof,
              financialStatements: app.financialStatements,
              cr12: app.cr12,
              companyProfile: app.companyProfile,
              bankReferenceLetter: app.bankReferenceLetter,
              entityType: app.entityType,
              directorsIds: app.directorsIds,
              practicingCertificates: app.practicingCertificates,
              keyMembersResumes: app.keyMembersResumes
            });
            setApplicationId(app._id);

            // Map backend data structure to frontend form structure
            // Start with all fields from app, then override with special mappings
            const mappedData = {
              // Basic Information - read directly from app or use defaults
              supplierName: app.supplierName || '',
              registeredCountry: app.registeredCountry || '',
              companyRegistrationNumber: app.companyRegistrationNumber || '',
              companyEmail: app.companyEmail || '',
              companyWebsite: app.companyWebsite || '',
              legalNature: mapEntityTypeToLegalNature(app.entityType),
              physicalAddress: app.physicalAddress || app.companyPhysicalAddress?.street || '',
              postalAddress: app.postalAddress || app.companyPhysicalAddress?.postalCode || '',

              // Contact Person (map from authorizedPerson if direct fields don't exist)
              contactFullName: app.contactFullName || app.authorizedPerson?.name || '',
              contactRelationship: app.contactRelationship || app.authorizedPerson?.relationship || '',
              contactIdPassport: app.contactIdPassport || app.authorizedPerson?.idPassportNumber || '',
              contactPhone: app.contactPhone || app.authorizedPerson?.phone || '',
              contactEmail: app.contactEmail || app.authorizedPerson?.email || '',

              // Payment Details - read directly from app
              bankName: app.bankName || '',
              bankNameOther: app.bankNameOther || '',
              accountNumber: app.accountNumber || '',
              branch: app.branch || '',
              currency: app.currency || '',
              creditPeriod: app.creditPeriod ? (() => {
                const periodMap = {
                  30: '30 Days',
                  45: '45 Days',
                  60: '60 Days',
                  90: '90 Days',
                  120: '120 Days',
                  // legacy values — map up to the minimum
                  7: '30 Days',
                  14: '30 Days',
                };
                return periodMap[app.creditPeriod] || `${app.creditPeriod} Days`;
              })() : '',

              // Entity Details
              entityType: mapEntityTypeToDisplay(app.entityType),
              serviceTypes: app.serviceTypes || app.serviceType || '',
              serviceTypesOther: app.serviceTypesOther || '',
              servicesDescription: app.servicesDescription || '',

              // Declarations — map legacy lowercase codes to display names
              sourceOfWealth: legacyWealthMap[app.sourceOfWealth] || app.sourceOfWealth || legacyWealthMap[app.sourceOfFunds?.source] || app.sourceOfFunds?.source || '',
              sourceOfWealthOther: app.sourceOfWealthOther || '',
              declarantFullName: app.declarantFullName || app.sourceOfFunds?.declarantName || '',
              declarantCapacity: app.declarantCapacity || app.sourceOfFunds?.declarantCapacity || '',
              declarantIdPassport: app.declarantIdPassport || app.sourceOfFunds?.declarantIdPassport || '',
              declarationDate: app.declarationDate || (app.sourceOfFunds?.declarationDate ? new Date(app.sourceOfFunds.declarationDate).toISOString().split('T')[0] : ''),
              consentToProcessing: app.consentToProcessing !== undefined ? app.consentToProcessing : (app.dataProcessingConsent?.granted || false),
              confirmInformationAccurate: app.confirmInformationAccurate || false,

              // File uploads — stored as filenames (strings) in the database.
              //
              // Built from the entity-type config rather than listed by hand, so
              // a document added to the checklist is loaded back without a
              // matching edit here. Keyed over the union of all entity types
              // (see allDocFields): Supplier is `strict: false`, so a draft can
              // be carrying filenames for a checklist it no longer matches, and
              // dropping them here would lose the supplier's uploads on the next
              // save.
              ...Object.fromEntries(allDocFields.map(({ field, uploadType }) => {
                const value = app[field];
                if (uploadType === 'multiple') {
                  return [field, Array.isArray(value)
                    ? value.filter((f) => f && typeof f === 'string' && f.trim() !== '')
                    : []];
                }
                return [field, (value && typeof value === 'string' && value.trim() !== '')
                  ? value
                  : null];
              })),
            };

            // Set form data - update all mapped fields
            setFormData(prev => {
              const updated = {
                ...prev,
                ...mappedData
              };
              console.log('Setting formData with file fields:', {
                certificateOfIncorporation: updated.certificateOfIncorporation,
                kraPinCertificate: updated.kraPinCertificate,
                etimsProof: updated.etimsProof,
                financialStatements: updated.financialStatements,
                cr12: updated.cr12,
                companyProfile: updated.companyProfile,
                bankReferenceLetter: updated.bankReferenceLetter,
                entityType: updated.entityType,
                directorsIds: updated.directorsIds,
                practicingCertificates: updated.practicingCertificates,
                keyMembersResumes: updated.keyMembersResumes
              });
              console.log('File field types after setting:', {
                certificateType: typeof updated.certificateOfIncorporation,
                certificateIsFile: updated.certificateOfIncorporation instanceof File,
                certificateIsString: typeof updated.certificateOfIncorporation === 'string',
                certificateValue: updated.certificateOfIncorporation
              });
              return updated;
            });


            // Set current month for date picker if date exists
            if (mappedData.declarationDate) {
              try {
                const date = parse(mappedData.declarationDate, 'yyyy-MM-dd', new Date());
                if (!isNaN(date.getTime())) {
                  setCurrentMonth(date);
                }
              } catch (e) {
                console.error('Error parsing date:', e);
              }
            }

            // A returned application always carries currentStep 4 — it was
            // saved there on submission — and step 4 is the read-only review.
            // Reopening it there left the supplier staring at a summary they
            // could not type into, with "Submit Application" as the only
            // button. Send them back to the start of the form instead; the
            // reviewer's request is shown above the stepper.
            if (app.status === 'more_info_required') {
              const asked = [...(app.approvalHistory || [])]
                .filter((h) => h.action === 'requested_info')
                .pop();
              setInfoRequest({
                comments: asked?.comments || '',
                department: asked?.approver?.department || '',
                timestamp: asked?.timestamp || null,
              });
              setActiveStep(0);
            } else if (app.currentStep !== undefined && app.currentStep !== null) {
              setActiveStep(app.currentStep);
            }
          }
        } catch (error) {
          console.error('Error loading application:', error);
          toast.error('Error loading application');
        }
      };
      loadApplication();
    } else {
      // Prefill contact and company information with registered user's details for new applications
      if (user) {
        const registeredFullName = user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`.trim()
          : (user.firstName || user.lastName || '');

        // Try to fetch existing supplier data to prefill company details
        const fetchSupplierForPrefill = async () => {
          try {
            const response = await api.get('/suppliers');
            const suppliers = response.data.data || [];
            if (suppliers.length > 0) {
              const supplierData = suppliers[0];
              const address = supplierData.companyPhysicalAddress;
              const fullAddress = address
                ? `${address.street || ''}, ${address.city || ''}, ${address.country || ''}${address.postalCode ? `, ${address.postalCode}` : ''}`.replace(/^,\s*|,\s*$/g, '')
                : supplierData.physicalAddress || '';

              setFormData(prev => ({
                ...prev,
                // Contact information
                contactFullName: registeredFullName,
                contactRelationship: supplierData.authorizedPerson?.relationship || '',
                contactIdPassport: supplierData.authorizedPerson?.idPassportNumber || '',
                contactPhone: supplierData.authorizedPerson?.phone || '',
                contactEmail: supplierData.authorizedPerson?.email || user.email || '',
                // Company information
                supplierName: supplierData.supplierName || '',
                registeredCountry: supplierData.registeredCountry || address?.country || '',
                companyRegistrationNumber: supplierData.companyRegistrationNumber || '',
                companyEmail: supplierData.companyEmail || '',
                companyWebsite: supplierData.companyWebsite || '',
                physicalAddress: fullAddress,
                entityType: mapEntityTypeToDisplay(supplierData.entityType) || '',
              }));
            } else {
              // No supplier data, just prefill contact info
              setFormData(prev => ({
                ...prev,
                contactFullName: registeredFullName,
                contactEmail: user.email || '',
              }));
            }
          } catch (error) {
            // On error, just prefill contact info
            setFormData(prev => ({
              ...prev,
              contactFullName: registeredFullName,
              contactEmail: user.email || '',
            }));
          }
        };

        fetchSupplierForPrefill();
      }
    }
  }, [id, user, entityTypeItems, allDocFields, navigate]);

  // Which uploads a reviewer sent back, keyed by the form field that owns them,
  // so the supplier is told which box to refill and why instead of being handed
  // a form that looks complete and a notification that does not say where to look.
  const [rejectedDocs, setRejectedDocs] = useState({});
  // Which Betika-provided forms are actually downloadable. The endpoint lists
  // only templates whose file is on disk, so a form that has not been supplied
  // yet simply shows no download button — the upload is still required.
  const [availableTemplates, setAvailableTemplates] = useState({});

  useEffect(() => {
    let cancelled = false;
    api.get('/templates')
      .then((res) => {
        if (cancelled) return;
        const byKey = {};
        for (const t of res.data?.data || []) byKey[t.key] = t;
        setAvailableTemplates(byKey);
      })
      .catch(() => {
        // A missing template list must not block the form; the upload still works.
        if (!cancelled) setAvailableTemplates({});
      });
    return () => { cancelled = true; };
  }, []);
  // The reviewer's last "we need more from you" note, shown above the stepper
  // so the supplier knows what to change before they start typing.
  const [infoRequest, setInfoRequest] = useState(null);

  useEffect(() => {
    if (!id) return;
    const loadRejections = async () => {
      try {
        const response = await api.get(`/documents/supplier/${id}`);
        const rows = response.data?.data || [];
        // Each field now owns a distinct document type, so this reverse lookup
        // no longer needs a tie-break. It used to: nationalId and
        // directorsNationalIds both wrote 'national_id' and the first one won,
        // so a reviewer rejecting a foreign company's director IDs put the
        // "Replace this document" note on the individual's National ID box —
        // a field that entity type never renders.
        const typeToField = allDocFields.reduce((acc, d) => {
          if (!acc[d.documentType]) acc[d.documentType] = d.field;
          return acc;
        }, {});

        const byField = {};
        rows
          .filter((doc) => doc.status === 'rejected')
          .forEach((doc) => {
            const field = typeToField[doc.documentType];
            if (field) byField[field] = doc.notes || 'The reviewer asked for a replacement.';
          });
        setRejectedDocs(byField);
      } catch {
        // A missing rejection list must not block editing the application.
        setRejectedDocs({});
      }
    };
    loadRejections();
  }, [id, allDocFields]);

  useEffect(() => {
    if (!id && user?.role === 'supplier' && user?.supplierApprovalStatus !== 'approved') {
      toast.error('Your registration is awaiting procurement approval before you can start a new application.');
      navigate('/dashboard');
    }
  }, [id, navigate, user]);


  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      // First, ensure we have a supplier ID (create draft if needed)
      let currentSupplierId = applicationId;
      if (!currentSupplierId) {
        // Create a minimal draft to get supplier ID
        const draftPayload = {
          supplierName: formData.supplierName || 'Draft Application',
          legalNature: mapEntityTypeToLegalNature(formData.entityType),
          serviceType: formData.serviceTypes === 'Other' ? (formData.serviceTypesOther || 'Other') : (formData.serviceTypes || 'professional_services'),
          serviceTypesOther: formData.serviceTypes === 'Other' ? formData.serviceTypesOther : '',
          currentStep: activeStep,
          lastModified: new Date().toISOString()
        };
        const createResponse = await api.post('/suppliers/draft', draftPayload);
        if (createResponse.data.data?._id) {
          currentSupplierId = createResponse.data.data._id;
          setApplicationId(currentSupplierId);
        } else {
          throw new Error('Failed to create supplier draft');
        }
      }

      // Upload all files first
      const updatedFormData = await uploadAllFiles(currentSupplierId);
      setFormData(updatedFormData);

      const payload = {
        ...updatedFormData,
        legalNature: mapEntityTypeToLegalNature(updatedFormData.entityType),
        status: 'draft',
        currentStep: activeStep,
        lastModified: new Date().toISOString()
      };

      // Ensure file fields are strings (filenames) or null, not File objects
      const fileFields = singleDocFields;

      fileFields.forEach(field => {
        if (payload[field] instanceof File) {
          payload[field] = payload[field].name;
        } else if (payload[field] === undefined || payload[field] === null) {
          if (typeof updatedFormData[field] === 'string' && updatedFormData[field].trim() !== '') {
            payload[field] = updatedFormData[field];
          } else {
            payload[field] = null;
          }
        }
      });

      // Handle array file fields
      const arrayFileFields = multiDocFields;
      arrayFileFields.forEach(field => {
        if (Array.isArray(payload[field]) && payload[field].length > 0) {
          payload[field] = payload[field].map(file => {
            if (file instanceof File) {
              return file.name;
            }
            return file;
          }).filter(f => f && typeof f === 'string' && f.trim() !== '');
        } else if (payload[field] === undefined) {
          if (Array.isArray(updatedFormData[field]) && updatedFormData[field].length > 0) {
            payload[field] = updatedFormData[field].filter(f => f && typeof f === 'string' && f.trim() !== '');
          } else {
            payload[field] = [];
          }
        }
      });

      // Remove any null/undefined values that might cause issues, but keep empty strings
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined) {
          // Keep null for file uploads, but convert undefined to empty string for text fields
          if (!key.includes('File') && !key.includes('Ids') && !key.includes('Certificates') && !key.includes('Resumes')) {
            payload[key] = '';
          }
        }
      });

      await api.put(`/suppliers/${currentSupplierId}`, payload);
      toast.success('Draft saved successfully!');
    } catch (error) {
      console.error('Save draft error:', error);
      toast.error(error.response?.data?.message || 'Error saving draft');
    } finally {
      setLoading(false);
    }
  };

  const validateRequiredDocuments = () => {
    if (!formData.entityType) {
      toast.error('Please select an entity type');
      return false;
    }

    const isSinglePresent = (v) =>
      v instanceof File || (typeof v === 'string' && v.trim() !== '');
    const isArrayPresent = (v) =>
      Array.isArray(v) ? v.some(isSinglePresent) : isSinglePresent(v);
    const isPresent = (doc) =>
      (doc.uploadType === 'multiple' ? isArrayPresent : isSinglePresent)(formData[doc.field]);

    const missing = [];
    const seenGroups = new Set();

    // Mirrors missingDocuments() in server/utils/documentRequirements.js, over
    // the same configured checklist — so the wizard asks for exactly what the
    // server will accept. The two used to disagree, and a sole proprietor could
    // clear this check and still be refused at submit.
    for (const doc of entityTypeDocs) {
      if (doc.required === false) continue;

      if (doc.group) {
        if (seenGroups.has(doc.group)) continue;
        seenGroups.add(doc.group);

        const members = entityTypeDocs.filter((d) => d.group === doc.group);
        if (!members.some(isPresent)) {
          missing.push(members.map((d) => d.label).join(' or '));
        }
        continue;
      }

      if (!isPresent(doc)) missing.push(doc.label);
    }

    if (missing.length > 0) {
      toast.error(`Missing required documents: ${missing.join(', ')}`);
      return false;
    }

    return true;
  };

  const validateStep = (step) => {
    const missing = [];

    // Mirrors missingFields() in server/utils/documentRequirements.js over the
    // same configured rules, so the wizard asks for exactly what the server will
    // accept. Fields with no config keep their previous hard-coded behaviour.
    const checkConfigured = (name, value, label, format) => {
      if (isHidden(name)) return;
      const present = CHECKBOX_FIELDS.has(name)
        ? value === true
        : String(value ?? '').trim() !== '';
      if (!present) {
        if (isRequiredField(name)) missing.push(label);
        return;
      }
      if (format === 'kenyaCompanyReg' && !isValidKenyaCompanyReg(value)) {
        missing.push(`${label} (invalid format — ${KENYA_REG_HELPER})`);
      }
      if (format === 'kenyaIdOrPassport' && !isValidKenyaIdOrPassport(value)) {
        missing.push(`${label} (invalid format — ${KENYA_ID_HELPER})`);
      }
    };

    if (step === 0) {
      if (!formData.supplierName?.trim()) missing.push('Company Name');
      checkConfigured('registeredCountry', formData.registeredCountry, 'Country of Incorporation');
      checkConfigured('companyRegistrationNumber', formData.companyRegistrationNumber,
        'Company Registration Number', formatFor('companyRegistrationNumber'));
      checkConfigured('companyEmail', formData.companyEmail, 'Company Email Address');
      checkConfigured('companyWebsite', formData.companyWebsite, 'Company Website');
      checkConfigured('physicalAddress', formData.physicalAddress, 'Physical Address');
      checkConfigured('postalAddress', formData.postalAddress, 'Postal Address');

      // Contact person stays hard-coded: it does not vary by entity type.
      if (!formData.contactFullName?.trim()) missing.push('Contact Person Full Name');
      if (!formData.contactRelationship?.trim()) missing.push('Contact Person Relationship');
      if (!formData.contactIdPassport?.trim()) {
        missing.push('Contact Person ID/Passport Number');
      } else if (!isValidKenyaIdOrPassport(formData.contactIdPassport)) {
        missing.push('Contact Person ID/Passport Number (invalid format — ' + KENYA_ID_HELPER + ')');
      }
      if (!formData.contactPhone?.trim()) missing.push('Contact Person Phone Number');
      if (!formData.contactEmail?.trim()) missing.push('Contact Person Email');
    }

    if (step === 1) {
      if (!formData.bankName?.trim()) missing.push('Bank Name');
      if (formData.bankName === 'Other' && !formData.bankNameOther?.trim()) missing.push('Please specify the bank name');
      if (!formData.accountNumber?.trim()) missing.push('Account Number');
      if (!formData.branch?.trim()) missing.push('Branch');
      if (!formData.currency?.trim()) missing.push('Currency');
      if (!formData.creditPeriod) missing.push('Credit Period');
    }

    if (step === 2) {
      if (!formData.entityType?.trim()) missing.push('Entity Type');
      if (!formData.serviceTypes?.trim()) missing.push('Service Type');
      if (formData.serviceTypes === 'Other' && !formData.serviceTypesOther?.trim()) missing.push('Please specify the type of service offered');
      checkConfigured('servicesDescription', formData.servicesDescription, 'Services Description');
    }

    if (step === 3) {
      checkConfigured('sourceOfWealth', formData.sourceOfWealth, 'Source of Wealth/Funds');
      if (formData.sourceOfWealth === 'Other' && !formData.sourceOfWealthOther?.trim()) {
        missing.push('Please specify source of wealth/funds');
      }
      checkConfigured('declarantFullName', formData.declarantFullName, 'Full Name of Declarant');
      checkConfigured('declarantCapacity', formData.declarantCapacity, 'Declarant Capacity');
      checkConfigured('declarantIdPassport', formData.declarantIdPassport,
        'Declarant ID/Passport Number', formatFor('declarantIdPassport'));
      checkConfigured('declarationDate', formData.declarationDate, 'Declaration Date');
      checkConfigured('consentToProcessing', formData.consentToProcessing,
        'Consent to Processing of Personal Information');
      checkConfigured('confirmInformationAccurate', formData.confirmInformationAccurate,
        'Confirmation that Information is Accurate');
    }

    return missing;
  };

  const handleSaveAndContinue = async () => {
    // Validate all required fields for the current step
    const missingFields = validateStep(activeStep);
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`, { autoClose: 6000 });
      return;
    }

    // Validate email and phone format
    const fieldsToCheck = ['companyEmail', 'contactPhone', 'contactEmail'];
    const newErrors = {};
    fieldsToCheck.forEach(f => {
      const err = validateInputField(f, formData[f]);
      if (err) newErrors[f] = err;
    });
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(prev => ({ ...prev, ...newErrors }));
      toast.error('Please fix the highlighted fields before continuing');
      return;
    }

    setLoading(true);
    try {
      if (activeStep === 2 || activeStep === steps.length - 1) {
        const ok = validateRequiredDocuments();
        if (!ok) {
          setLoading(false);
          return;
        }
      }

      // First, ensure we have a supplier ID (create draft if needed)
      let currentSupplierId = applicationId;
      if (!currentSupplierId) {
        // Create a minimal draft to get supplier ID
        const draftPayload = {
          supplierName: formData.supplierName || 'Draft Application',
          legalNature: mapEntityTypeToLegalNature(formData.entityType),
          serviceType: formData.serviceTypes === 'Other' ? (formData.serviceTypesOther || 'Other') : (formData.serviceTypes || 'professional_services'),
          serviceTypesOther: formData.serviceTypes === 'Other' ? formData.serviceTypesOther : '',
          currentStep: activeStep,
          lastModified: new Date().toISOString()
        };
        const createResponse = await api.post('/suppliers/draft', draftPayload);
        if (createResponse.data.data?._id) {
          currentSupplierId = createResponse.data.data._id;
          setApplicationId(currentSupplierId);
        } else {
          throw new Error('Failed to create supplier draft');
        }
      }

      // Upload all files first
      const updatedFormData = await uploadAllFiles(currentSupplierId);
      setFormData(updatedFormData);

      // Create payload with ALL form data from all steps (now with uploaded filenames)
      const payload = {
        ...updatedFormData,
        legalNature: mapEntityTypeToLegalNature(updatedFormData.entityType),
        status: activeStep === steps.length - 1 ? 'submitted' : 'draft',
        currentStep: activeStep, // Save the current step the user is on
        lastModified: new Date().toISOString()
      };

      // Ensure file fields are strings (filenames) or null, not File objects
      const fileFields = singleDocFields;

      fileFields.forEach(field => {
        if (payload[field] instanceof File) {
          // This shouldn't happen after upload, but handle it just in case
          payload[field] = payload[field].name;
        } else if (payload[field] === undefined || payload[field] === null) {
          // Preserve existing filename if it's a non-empty string, otherwise keep null
          if (typeof updatedFormData[field] === 'string' && updatedFormData[field].trim() !== '') {
            payload[field] = updatedFormData[field];
          } else {
            payload[field] = null;
          }
        }
      });

      // Handle array file fields
      const arrayFileFields = multiDocFields;
      arrayFileFields.forEach(field => {
        if (Array.isArray(payload[field]) && payload[field].length > 0) {
          // Ensure all items are strings (filenames), not File objects
          payload[field] = payload[field].map(file => {
            if (file instanceof File) {
              return file.name;
            }
            return file;
          }).filter(f => f && typeof f === 'string' && f.trim() !== '');
        } else if (payload[field] === undefined) {
          // Preserve existing array if it has valid filenames
          if (Array.isArray(updatedFormData[field]) && updatedFormData[field].length > 0) {
            payload[field] = updatedFormData[field].filter(f => f && typeof f === 'string' && f.trim() !== '');
          } else {
            payload[field] = [];
          }
        }
      });

      // Ensure all fields are included, even if empty
      // Remove undefined values but keep empty strings, null for files, and false for booleans
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          // Convert undefined to empty string for text fields, keep null for file uploads
          if (!key.includes('File') && !key.includes('Ids') && !key.includes('Certificates') && !key.includes('Resumes')) {
            payload[key] = '';
          } else {
            payload[key] = null;
          }
        }
      });

      console.log('Saving payload with all fields:', Object.keys(payload));
      console.log('File fields being saved:', {
        certificateOfIncorporation: payload.certificateOfIncorporation,
        kraPinCertificate: payload.kraPinCertificate,
        etimsProof: payload.etimsProof,
        entityType: payload.entityType,
        directorsIds: payload.directorsIds,
        practicingCertificates: payload.practicingCertificates,
        keyMembersResumes: payload.keyMembersResumes
      });
      console.log('Full payload file fields check:', {
        hasCertificate: !!payload.certificateOfIncorporation,
        hasKraPin: !!payload.kraPinCertificate,
        hasEtims: !!payload.etimsProof,
        hasFinancial: !!payload.financialStatements,
        hasCr12: !!payload.cr12,
        hasProfile: !!payload.companyProfile,
        hasBankRef: !!payload.bankReferenceLetter,
        directorsCount: Array.isArray(payload.directorsIds) ? payload.directorsIds.length : 0
      });

      // Check if this is the last step (submission)
      if (activeStep === steps.length - 1) {
        // Submit application - save data and submit
        payload.status = 'submitted';
        await api.put(`/suppliers/${currentSupplierId}`, payload);
        await api.post(`/suppliers/${currentSupplierId}/submit`, payload);
        toast.success('Application submitted successfully!');
        navigate('/dashboard');
      } else {
        // Not the last step - save and continue
        await api.put(`/suppliers/${currentSupplierId}`, payload);

        // Move to next step
        const nextStep = activeStep + 1;
        setActiveStep(nextStep);
        toast.success('Progress saved!');
      }
    } catch (error) {
      console.error('Save and continue error:', error);
      // The server validates every field and document requirement at submit,
      // not just the current step, so a refusal here can name items the wizard
      // itself never flagged. Render the list when the server sends one, so the
      // supplier has somewhere to go instead of a message with no next step.
      const missing = error.response?.data?.missing;
      if (Array.isArray(missing) && missing.length > 0) {
        toast.error(`Please provide: ${missing.join(', ')}`, { autoClose: 8000 });
      } else {
        toast.error(error.response?.data?.message || 'Error saving application');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = async () => {
    if (activeStep > 0) {
      // Save current form data before navigating back to ensure nothing is lost
      if (applicationId) {
        try {
          // Create payload with current form data
          const payload = {
            ...formData,
            currentStep: activeStep,
            lastModified: new Date().toISOString()
          };

          // Handle file objects - extract filenames for storage
          const fileFields = singleDocFields;

          fileFields.forEach(field => {
            if (payload[field] instanceof File) {
              // Convert File object to filename string
              payload[field] = payload[field].name;
            } else if (payload[field] === undefined || payload[field] === null) {
              // Preserve existing filename if it's a string, otherwise keep null
              // This ensures that when navigating, we don't lose previously saved filenames
              if (typeof formData[field] === 'string' && formData[field].trim() !== '') {
                payload[field] = formData[field];
              } else {
                payload[field] = null;
              }
            }
            // If payload[field] is already a string (from previous load), keep it as is
          });

          // Handle array file fields
          const arrayFileFields = multiDocFields;
          arrayFileFields.forEach(field => {
            if (Array.isArray(payload[field]) && payload[field].length > 0) {
              if (payload[field][0] instanceof File) {
                payload[field] = payload[field].map(file => file.name);
              }
            } else if (payload[field] === undefined) {
              payload[field] = Array.isArray(formData[field]) ? formData[field] : [];
            }
          });

          // Update currentStep to the previous step
          const previousStep = activeStep - 1;
          payload.currentStep = previousStep;

          // Save silently (no toast) to avoid interrupting navigation
          await api.put(`/suppliers/${applicationId}`, payload);
        } catch (error) {
          console.error('Error saving before navigating back:', error);
          // Continue with navigation even if save fails
        }
      }

      // Navigate to previous step - formData will remain intact
      setActiveStep(prev => prev - 1);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {/* Basic Information Section */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 3,
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: '18px',
                  color: '#111827'
                }}
              >
                Basic Information
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontSize: '14px',
                  mb: 3
                }}
              >
                Summary of your company
              </Typography>

              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Company name
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.supplierName}
                    onChange={(e) => {
                      handleChange('supplierName', e.target.value);
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>

              {!isHidden('registeredCountry') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Country of Incorporation
                  </Typography>
                  <ClickAwayListener onClickAway={() => setCountrySearchOpen(false)}>
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        value={selectedCountry ? selectedCountry.name : ''}
                        placeholder="Select country"
                        onClick={(e) => {
                          setCountryAnchorEl(e.currentTarget);
                          setCountrySearchOpen(!countrySearchOpen);
                          setCountrySearchTerm('');
                        }}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <KeyboardArrowDown sx={{ color: '#6b7280' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                          }
                        }}
                      />

                      {countrySearchOpen && (
                        <Paper
                          sx={{
                            mt: 0.5,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            borderRadius: '6px',
                            overflow: 'hidden',
                          }}
                        >
                          {/* Search Input Inside Dropdown */}
                          <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Search country..."
                              value={countrySearchTerm}
                              onChange={(e) => setCountrySearchTerm(e.target.value)}
                              autoFocus
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Search sx={{ color: '#9ca3af', fontSize: 20 }} />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'transparent',
                                  '& fieldset': {
                                    border: 'none',
                                  },
                                  '&:hover fieldset': {
                                    border: 'none',
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none',
                                  },
                                  padding: 0,
                                }
                              }}
                            />
                          </Box>

                          {/* Country list — scrolls; the search box above is
                              how you get through 249 of them quickly. */}
                          <Box
                            sx={{
                              maxHeight: '280px',
                              overflowY: 'auto',
                              px: 1.5,
                              pb: 1,
                              '&::-webkit-scrollbar': {
                                width: '6px',
                              },
                              '&::-webkit-scrollbar-track': {
                                backgroundColor: '#f1f1f1',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#d1d5db',
                                borderRadius: '3px',
                              },
                            }}
                          >
                            {filteredCountries.length > 0 ? (
                              filteredCountries.map((country) => (
                                <Box
                                  key={country.code}
                                  onClick={() => {
                                    handleChange('registeredCountry', country.name);
                                    setCountrySearchOpen(false);
                                    setCountrySearchTerm('');
                                  }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 1.5,
                                    py: 1.25,
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    '&:hover': {
                                      backgroundColor: '#f9fafb'
                                    }
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography sx={{ fontSize: '20px' }}>{country.flag}</Typography>
                                    <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                                      {country.name}
                                    </Typography>
                                  </Box>
                                  {formData.registeredCountry === country.name && (
                                    <Check sx={{ color: '#578A18', fontSize: 20 }} />
                                  )}
                                </Box>
                              ))
                            ) : (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '14px', color: '#9ca3af' }}>
                                  No countries found
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  </ClickAwayListener>
                </Grid>
              )}

              {!isHidden('companyRegistrationNumber') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Company Registration Number
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.companyRegistrationNumber}
                    onChange={(e) => {
                      handleChange('companyRegistrationNumber', e.target.value);
                    }}
                    size="small"
                    error={!!formData.companyRegistrationNumber && !isValidKenyaCompanyReg(formData.companyRegistrationNumber)}
                    helperText={
                      formData.companyRegistrationNumber && !isValidKenyaCompanyReg(formData.companyRegistrationNumber)
                        ? KENYA_REG_HELPER
                        : 'e.g. CPR/2022/123456, PVT/2021/123456, BN-123456'
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>
              )}

              {!isHidden('companyEmail') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Company Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={formData.companyEmail}
                    onChange={(e) => {
                      handleChange('companyEmail', e.target.value);
                    }}
                    onBlur={() => handleFieldBlur('companyEmail')}
                    error={!!fieldErrors.companyEmail}
                    helperText={fieldErrors.companyEmail}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>
              )}

              {!isHidden('companyWebsite') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Company Website <Box component="span" sx={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</Box>
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.companyWebsite}
                    onChange={(e) => {
                      handleChange('companyWebsite', e.target.value);
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>
              )}

              {!isHidden('postalAddress') && (
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Postal Address
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.postalAddress}
                    onChange={(e) => handleChange('postalAddress', e.target.value)}
                    placeholder="e.g. P.O. Box 12345-00100, Nairobi"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                  />
                </Grid>
              )}

              {!isHidden('physicalAddress') && (
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Physical Address
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.physicalAddress}
                    onChange={(e) => {
                      handleChange('physicalAddress', e.target.value);
                    }}
                    placeholder="Type your physical address here. Be as detailed as possible"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>
              )}
              </Grid>
            </Paper>

            {/* Contact Person Details Section */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 3,
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: '18px',
                  color: '#111827'
                }}
              >
                Contact Person Details
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontSize: '14px',
                  mb: 3
                }}
              >
                Details of person authorised to act on behalf of the entity
              </Typography>

              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Contact Person's Full Name
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ mb: 1, display: 'block', fontSize: '12px', color: '#6b7280' }}
                  >
                    Enter the full name of the individual contact person, not the company name
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.contactFullName}
                    onChange={(e) => {
                      handleChange('contactFullName', e.target.value);
                    }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Position / Role in Entity
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={formData.contactRelationship}
                      onChange={(e) => {
                        handleChange('contactRelationship', e.target.value);
                      }}
                      displayEmpty
                      IconComponent={KeyboardArrowDown}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-icon': { color: '#6b7280' },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select position
                      </MenuItem>
                      {contactRelationships.map((role) => (
                        <MenuItem key={role} value={role}>{role}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    ID/Passport Number
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.contactIdPassport}
                    onChange={(e) => {
                      handleChange('contactIdPassport', e.target.value);
                    }}
                    size="small"
                    error={!!formData.contactIdPassport && !isValidKenyaIdOrPassport(formData.contactIdPassport)}
                    helperText={
                      formData.contactIdPassport && !isValidKenyaIdOrPassport(formData.contactIdPassport)
                        ? KENYA_ID_HELPER
                        : 'National ID (7–8 digits) or passport number'
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Phone number
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.contactPhone}
                    onChange={(e) => {
                      handleChange('contactPhone', e.target.value);
                    }}
                    onBlur={() => handleFieldBlur('contactPhone')}
                    error={!!fieldErrors.contactPhone}
                    helperText={fieldErrors.contactPhone}
                    placeholder="e.g +254712345678"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Email address
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => {
                      handleChange('contactEmail', e.target.value);
                    }}
                    onBlur={() => handleFieldBlur('contactEmail')}
                    error={!!fieldErrors.contactEmail}
                    helperText={fieldErrors.contactEmail}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

          </Box>
        );

      case 1:
        return (
          <Box>
            {/* Payment Details Section */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 3,
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: '18px',
                  color: '#111827'
                }}
              >
                Payment Details
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontSize: '14px',
                  mb: 3
                }}
              >
                Payment method and terms
              </Typography>

              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Bank name
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={formData.bankName}
                      onChange={(e) => handleChange('bankName', e.target.value)}
                      displayEmpty
                      sx={{ backgroundColor: '#fff' }}
                    >
                      <MenuItem value="" disabled>Select bank</MenuItem>
                      {bankNameOptions.length === 0 && (
                        <MenuItem value="" disabled>Loading...</MenuItem>
                      )}
                      {bankNameOptions.map((b) => (
                        <MenuItem key={b} value={b}>{b}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.bankName === 'Other' && (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter bank name"
                      value={formData.bankNameOther}
                      onChange={(e) => handleChange('bankNameOther', e.target.value)}
                      sx={{ mt: 1, '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                    />
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Account Number
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.accountNumber}
                    onChange={(e) => handleChange('accountNumber', e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Branch
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.branch}
                    onChange={(e) => handleChange('branch', e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Currency
                  </Typography>
                  {/* Searchable: the list carries every country's currency. The
                      stored value stays the bare code, as before. */}
                  <Autocomplete
                    size="small"
                    options={currencyItems}
                    value={currencyItems.find((c) => c.name === formData.currency) || null}
                    onChange={(e, option) => handleChange('currency', option ? option.name : '')}
                    getOptionLabel={(c) => (c.description ? `${c.name} — ${c.description}` : c.name)}
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    noOptionsText={currencyItems.length ? 'No currencies found' : 'Loading...'}
                    popupIcon={<KeyboardArrowDown sx={{ color: '#6b7280' }} />}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search by code or name"
                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Applicable Credit Period
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={formData.creditPeriod}
                      onChange={(e) => handleChange('creditPeriod', e.target.value)}
                      displayEmpty
                      IconComponent={KeyboardArrowDown}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-icon': {
                          color: '#6b7280'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select
                      </MenuItem>
                      {creditPeriods.map((period) => (
                        <MenuItem key={period} value={period}>
                          {period}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        );

      case 2: {

        return (
          <Box>
            {/* Entity Details Section */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 3,
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: '18px',
                  color: '#111827'
                }}
              >
                Entity Details
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontSize: '14px',
                  mb: 3
                }}
              >
                Entity type and required documents
              </Typography>

              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Entity type
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={formData.entityType}
                      onChange={(e) => {
                        handleChange('entityType', e.target.value);
                      }}
                      displayEmpty
                      IconComponent={KeyboardArrowDown}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-icon': { color: '#6b7280' },
                      }}
                    >
                      <MenuItem value="" disabled>Select</MenuItem>
                      {entityTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                      {entityTypes.length === 0 && (
                        <MenuItem disabled>Loading...</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Registered Company Name
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.supplierName}
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText="As captured in your company registration documents"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f9fafb',
                        cursor: 'not-allowed',
                        '& .MuiInputBase-input': { cursor: 'not-allowed' }
                      }
                    }}
                  />
                </Grid>

              {!isHidden('companyRegistrationNumber') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Company Registration Number
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.companyRegistrationNumber}
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText="As captured in your certificate of incorporation"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f9fafb',
                        cursor: 'not-allowed',
                        '& .MuiInputBase-input': { cursor: 'not-allowed' }
                      }
                    }}
                  />
                </Grid>
              )}

                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#111827',
                      mt: 3
                    }}
                  >
                    Required Documents
                  </Typography>
                </Grid>

                {/* Required Documents - Dynamically Balanced Two Columns */}
                {(() => {
                  const documents = getRequiredDocuments();
                  const midpoint = Math.ceil(documents.length / 2);
                  const leftDocs = documents.slice(0, midpoint);
                  const rightDocs = documents.slice(midpoint);

                  return (
                    <>
                      <Grid item xs={12} md={6}>
                        {leftDocs.map((doc, idx) => (
                          <div key={`left-${idx}`}>
                            {renderDocument(doc)}
                          </div>
                        ))}
                      </Grid>

                      {rightDocs.length > 0 && (
                        <Grid item xs={12} md={6}>
                          {rightDocs.map((doc, idx) => (
                            <div key={`right-${idx}`}>
                              {renderDocument(doc)}
                            </div>
                          ))}
                        </Grid>
                      )}
                    </>
                  );
                })()}

              </Grid>
            </Paper>

            {/* Service Details Section */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 3,
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: '18px',
                  color: '#111827'
                }}
              >
                Service Details
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontSize: '14px',
                  mb: 3
                }}
              >
                Details of services being offered
              </Typography>

              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Types of Services Being Offered
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={formData.serviceTypes}
                      onChange={(e) => handleChange('serviceTypes', e.target.value)}
                      displayEmpty
                      IconComponent={KeyboardArrowDown}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-icon': {
                          color: '#6b7280'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>Select</MenuItem>
                      {serviceTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                      {serviceTypeOptions.length === 0 && (
                        <MenuItem disabled>Loading...</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                {formData.serviceTypes === 'Other' && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                      Please specify service type <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={formData.serviceTypesOther}
                      onChange={(e) => handleChange('serviceTypesOther', e.target.value)}
                      placeholder="Describe the type of service being offered"
                      sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
                    />
                  </Grid>
                )}

                {!isHidden('servicesDescription') && (
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Services Description
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={formData.servicesDescription}
                    onChange={(e) => handleChange('servicesDescription', e.target.value)}
                    placeholder="Describe the services your company provides. Be as detailed as possible"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        );
      }

      case 3:
        return (
          <Box>
            {/* Source of Funds Declaration Section */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 3,
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '18px',
                  color: '#111827'
                }}
              >
                Source of Funds Declaration & Processing of Personal Information
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#374151',
                  fontSize: '14px',
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                I, in my personal capacity/representative capacity, hereby declare and confirm that the funds paid and/or to be paid, by the entity to offer goods/services with Shop and Deliver Limited have not been derived from any criminal activities of any nature whatsoever.
              </Typography>

              <Grid container spacing={2.5}>
                {!isHidden('sourceOfWealth') && (
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Source of wealth/Funds
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={formData.sourceOfWealth}
                      onChange={(e) => handleChange('sourceOfWealth', e.target.value)}
                      displayEmpty
                      IconComponent={KeyboardArrowDown}
                      sx={{
                        backgroundColor: '#fff',
                        '& .MuiSelect-icon': {
                          color: '#6b7280'
                        }
                      }}
                    >
                      <MenuItem value="" disabled>Select</MenuItem>
                      {wealthSourceOptions.map((src) => (
                        <MenuItem key={src} value={src}>{src}</MenuItem>
                      ))}
                      {wealthSourceOptions.length === 0 && (
                        <MenuItem disabled>Loading...</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                )}

                {formData.sourceOfWealth === 'Other' && (
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                    >
                      Please specify source of wealth/funds
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Describe your source of wealth/funds"
                      value={formData.sourceOfWealthOther}
                      onChange={(e) => handleChange('sourceOfWealthOther', e.target.value)}
                      sx={{ backgroundColor: '#fff' }}
                    />
                  </Grid>
                )}

                {!isHidden('declarantFullName') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Full Name of Declarant
                  </Typography>
                  <Typography variant="caption" sx={{ mb: 1, display: 'block', fontSize: '12px', color: '#6b7280' }}>
                    Enter the individual's name, not the company name
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.declarantFullName}
                    onChange={(e) => handleChange('declarantFullName', e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>
                )}

                {!isHidden('declarantCapacity') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Capacity / Position in Entity
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={formData.declarantCapacity}
                      onChange={(e) => handleChange('declarantCapacity', e.target.value)}
                      displayEmpty
                      sx={{ backgroundColor: '#fff' }}
                    >
                      <MenuItem value="" disabled>Select position</MenuItem>
                      {contactRelationships.map((role) => (
                        <MenuItem key={role} value={role}>{role}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                )}

                {!isHidden('declarantIdPassport') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    ID/Passport Number
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.declarantIdPassport}
                    onChange={(e) => handleChange('declarantIdPassport', e.target.value)}
                    size="small"
                    error={!!formData.declarantIdPassport && !isValidKenyaIdOrPassport(formData.declarantIdPassport)}
                    helperText={
                      formData.declarantIdPassport && !isValidKenyaIdOrPassport(formData.declarantIdPassport)
                        ? KENYA_ID_HELPER
                        : 'National ID (7–8 digits) or passport number'
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                </Grid>
                )}

                {!isHidden('declarationDate') && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#374151' }}
                  >
                    Date
                  </Typography>
                  <ClickAwayListener onClickAway={() => setDatePickerOpen(false)}>
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        fullWidth
                        type="text"
                        value={formatDateForDisplay(formData.declarationDate)}
                        onClick={handleDateClick}
                        size="small"
                        readOnly
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarToday sx={{ color: '#6b7280' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            '& input': {
                              cursor: 'pointer',
                            }
                          }
                        }}
                      />

                      {datePickerOpen && (
                        <Paper
                          sx={{
                            mt: 0.5,
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            zIndex: 1300,
                            backgroundColor: '#fff',
                            minWidth: '300px',
                          }}
                        >
                          {/* Calendar Header */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #e5e7eb' }}>
                            <IconButton
                              size="small"
                              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                              sx={{ color: '#6b7280' }}
                            >
                              <NavigateBefore />
                            </IconButton>
                            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                              {format(currentMonth, 'MMMM yyyy')}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                              sx={{ color: '#6b7280' }}
                            >
                              <NavigateNext />
                            </IconButton>
                          </Box>

                          {/* Calendar Days Header */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, p: 1, backgroundColor: '#f9fafb' }}>
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                              <Typography
                                key={day}
                                sx={{
                                  textAlign: 'center',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#6b7280',
                                  py: 1
                                }}
                              >
                                {day}
                              </Typography>
                            ))}
                          </Box>

                          {/* Calendar Days */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, p: 1 }}>
                            {calendarDays().map((day, idx) => {
                              const isCurrentMonth = isSameMonth(day, currentMonth);
                              let isSelected = false;
                              if (formData.declarationDate) {
                                try {
                                  const selectedDate = parse(formData.declarationDate, 'yyyy-MM-dd', new Date());
                                  isSelected = isSameDay(day, selectedDate);
                                } catch (e) {
                                  // Invalid date format
                                }
                              }
                              const isToday = isSameDay(day, new Date());

                              return (
                                <Box
                                  key={idx}
                                  onClick={() => isCurrentMonth && handleDateSelect(day)}
                                  sx={{
                                    aspectRatio: '1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: isCurrentMonth ? 'pointer' : 'default',
                                    borderRadius: '4px',
                                    backgroundColor: isSelected ? theme.palette.green.main : 'transparent',
                                    color: isSelected ? '#fff' : isToday && isCurrentMonth ? theme.palette.green.main : isCurrentMonth ? '#374151' : '#d1d5db',
                                    fontWeight: isToday || isSelected ? 700 : 400,
                                    border: isToday && !isSelected ? `1.5px solid ${theme.palette.green.main}` : '1.5px solid transparent',
                                    fontSize: '14px',
                                    '&:hover': {
                                      backgroundColor: isCurrentMonth && !isSelected ? '#f3f4f6' : undefined,
                                    }
                                  }}
                                >
                                  {format(day, 'd')}
                                </Box>
                              );
                            })}
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  </ClickAwayListener>
                </Grid>
                )}

                {/* Checkboxes */}
                {!isHidden('consentToProcessing') && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.consentToProcessing}
                        onChange={(e) => handleChange('consentToProcessing', e.target.checked)}
                        sx={{
                          color: theme.palette.green.main,
                          '&.Mui-checked': {
                            color: theme.palette.green.main,
                          }
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                        I consent to the processing of my personal information in accordance with the{' '}
                        <Link href="#" sx={{ color: theme.palette.green.main, textDecoration: 'underline' }}>
                          privacy policy
                        </Link>
                        {' '}and applicable data protection laws.
                      </Typography>
                    }
                  />
                </Grid>
                )}

                {!isHidden('confirmInformationAccurate') && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.confirmInformationAccurate}
                        onChange={(e) => handleChange('confirmInformationAccurate', e.target.checked)}
                        sx={{
                          color: theme.palette.green.main,
                          '&.Mui-checked': {
                            color: theme.palette.green.main,
                          }
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                        I confirm that all information provided is true and accurate to the best of my knowledge.
                      </Typography>
                    }
                  />
                </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 3,
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: '18px',
                  color: '#111827'
                }}
              >
                Review Application
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  fontSize: '14px',
                  mb: 3
                }}
              >
                Confirm your application is okay before submitting
              </Typography>

              {/* Basic Information Accordion */}
              <Accordion
                expanded={expandedAccordion === 'basicInformation'}
                onChange={(event, isExpanded) => {
                  setExpandedAccordion(isExpanded ? 'basicInformation' : '');
                }}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderBottom: '1px solid #e5e7eb',
                  borderRadius: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      my: 2
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                    Basic Information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Company Name
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.supplierName || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Country of Incorporation
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.registeredCountry || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Company Email Address
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.companyEmail || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Company Website
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.companyWebsite || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Company Registration Number
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.companyRegistrationNumber || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Physical Address
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.physicalAddress || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Contact Details Accordion */}
              <Accordion
                expanded={expandedAccordion === 'contactDetails'}
                onChange={(event, isExpanded) => {
                  setExpandedAccordion(isExpanded ? 'contactDetails' : '');
                }}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderBottom: '1px solid #e5e7eb',
                  borderRadius: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      my: 2
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                    Contact Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Full name
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.contactFullName || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Relationship to Entity
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.contactRelationship || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        ID/Passport Number
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.contactIdPassport || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Phone number
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.contactPhone || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Email address
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.contactEmail || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Payment Details Accordion */}
              <Accordion
                expanded={expandedAccordion === 'paymentDetails'}
                onChange={(event, isExpanded) => {
                  setExpandedAccordion(isExpanded ? 'paymentDetails' : '');
                }}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderBottom: '1px solid #e5e7eb',
                  borderRadius: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      my: 2
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                    Payment Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Bank name
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.bankName === 'Other'
                          ? (formData.bankNameOther || 'Other')
                          : (formData.bankName || '-')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Account Number
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.accountNumber || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Branch
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.branch || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Currency
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.currency || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Applicable Credit Period
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.creditPeriod || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Entity Details Accordion */}
              <Accordion
                expanded={expandedAccordion === 'entityDetails'}
                onChange={(event, isExpanded) => {
                  setExpandedAccordion(isExpanded ? 'entityDetails' : '');
                }}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderBottom: '1px solid #e5e7eb',
                  borderRadius: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      my: 2
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                    Entity Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Entity type
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151', mb: 2 }}>
                        {formData.entityType || '-'}
                      </Typography>
                    </Grid>

                    {/* Certificate of Incorporation Documents */}
                    {(() => {
                      const renderDocumentCard = (fileName, documentName) => {
                        if (!fileName) return null;
                        const fileExtension = (fileName instanceof File ? fileName.name : typeof fileName === 'string' ? fileName : '').split('.').pop()?.toUpperCase() || 'PDF';

                        return (
                          <Box
                            key={documentName}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1.5,
                              mb: 1,
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              backgroundColor: '#fff',
                              '&:hover': {
                                borderColor: '#d1d5db',
                                backgroundColor: '#f9fafb'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                              <Box
                                component="img"
                                src="/images/File.svg"
                                alt="File icon"
                                sx={{ width: 24, height: 24, color: '#6b7280' }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500, mb: 0.25 }}>
                                  {documentName}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
                                  {fileExtension} • {fileName instanceof File && fileName.size ? `${(fileName.size / (1024 * 1024)).toFixed(1)} MB` : '2.3 MB'}
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleViewFile(fileName, documentName)}
                              sx={{
                                color: '#6b7280',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: '#374151'
                                }
                              }}
                            >
                              <Box
                                component="img"
                                src="/images/eye.svg"
                                alt="View icon"
                                sx={{ width: 20, height: 20 }}
                              />
                            </IconButton>
                          </Box>
                        );
                      };

                      const renderMultipleDocumentCards = (files, documentName) => {
                        if (!files || files.length === 0) return null;
                        return files.map((file, index) => {
                          const displayName = files.length > 1
                            ? `${documentName} ${index + 1}`
                            : documentName;
                          const fileExtension = (file instanceof File ? file.name : typeof file === 'string' ? file : '').split('.').pop()?.toUpperCase() || 'PDF';

                          return (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                mb: 1,
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                backgroundColor: '#fff',
                                '&:hover': {
                                  borderColor: '#d1d5db',
                                  backgroundColor: '#f9fafb'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                <Box
                                  component="img"
                                  src="/images/File.svg"
                                  alt="File icon"
                                  sx={{ width: 24, height: 24, color: '#6b7280' }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500, mb: 0.25 }}>
                                    {displayName}
                                  </Typography>
                                  <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
                                    {fileExtension} • {file instanceof File && file.size ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : '2.3 MB'}
                                  </Typography>
                                </Box>
                              </Box>
                              <IconButton
                                size="small"
                                sx={{
                                  color: '#6b7280',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: '#374151'
                                  }
                                }}
                              >
                                <Box
                                  component="img"
                                  src="/images/eye.svg"
                                  alt="View icon"
                                  sx={{ width: 20, height: 20 }}
                                />
                              </IconButton>
                            </Box>
                          );
                        });
                      };

                      return (
                        <Grid item xs={12}>
                          {getRequiredDocuments().map((doc) => {
                            if (doc.type === 'multi') {
                              const files = formData[doc.field];
                              return files?.length > 0
                                ? renderMultipleDocumentCards(files, doc.label)
                                : null;
                            }
                            return renderDocumentCard(formData[doc.field], doc.label);
                          })}
                        </Grid>
                      );
                    })()}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Service Details Accordion */}
              <Accordion
                expanded={expandedAccordion === 'serviceDetails'}
                onChange={(event, isExpanded) => {
                  setExpandedAccordion(isExpanded ? 'serviceDetails' : '');
                }}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderBottom: '1px solid #e5e7eb',
                  borderRadius: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      my: 2
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                    Service Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Types of Services Being Offered
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151', mb: 2 }}>
                        {formData.serviceTypes === 'Other'
                          ? (formData.serviceTypesOther || 'Other')
                          : (formData.serviceTypes || '-')}
                      </Typography>
                    </Grid>

                    {/* Service Documents */}
                    {(() => {

                      const renderMultipleDocumentCards = (files, documentName) => {
                        if (!files || files.length === 0) return null;
                        return files.map((file, index) => {
                          const displayName = files.length > 1
                            ? `${documentName} ${index + 1}`
                            : documentName;
                          const fileExtension = (file instanceof File ? file.name : typeof file === 'string' ? file : '').split('.').pop()?.toUpperCase() || 'PDF';

                          return (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                mb: 1,
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                backgroundColor: '#fff',
                                '&:hover': {
                                  borderColor: '#d1d5db',
                                  backgroundColor: '#f9fafb'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                <Box
                                  component="img"
                                  src="/images/File.svg"
                                  alt="File icon"
                                  sx={{ width: 24, height: 24, color: '#6b7280' }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500, mb: 0.25 }}>
                                    {displayName}
                                  </Typography>
                                  <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>
                                    {fileExtension} • {file instanceof File && file.size ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : '2.3 MB'}
                                  </Typography>
                                </Box>
                              </Box>
                              <IconButton
                                size="small"
                                sx={{
                                  color: '#6b7280',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: '#374151'
                                  }
                                }}
                              >
                                <Box
                                  component="img"
                                  src="/images/eye.svg"
                                  alt="View icon"
                                  sx={{ width: 20, height: 20 }}
                                />
                              </IconButton>
                            </Box>
                          );
                        });
                      };

                      return (
                        <Grid item xs={12}>
                          {/* Practicing Certificates */}
                          {formData.practicingCertificates && formData.practicingCertificates.length > 0 && renderMultipleDocumentCards(formData.practicingCertificates, 'Practicing Certificate')}
                          {/* Key Member's Resumes */}
                          {formData.keyMembersResumes && formData.keyMembersResumes.length > 0 && renderMultipleDocumentCards(formData.keyMembersResumes, 'Resume')}
                        </Grid>
                      );
                    })()}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Source of Funds Declaration Accordion */}
              <Accordion
                expanded={expandedAccordion === 'sourceOfFunds'}
                onChange={(event, isExpanded) => {
                  setExpandedAccordion(isExpanded ? 'sourceOfFunds' : '');
                }}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderBottom: '1px solid #e5e7eb',
                  borderRadius: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      my: 2
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                    Source of Funds Declaration
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Source of wealth/Funds
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.sourceOfWealth === 'Other'
                          ? (formData.sourceOfWealthOther || 'Other')
                          : (formData.sourceOfWealth || '-')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Full Name of Declarant
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.declarantFullName || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Capacity
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.declarantCapacity || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        ID/Passport Number
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.declarantIdPassport || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Date
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.declarationDate ? (() => {
                          try {
                            const date = parse(formData.declarationDate, 'yyyy-MM-dd', new Date());
                            if (!isNaN(date.getTime())) {
                              return format(date, 'do MMMM, yyyy');
                            }
                            return formatDateForDisplay(formData.declarationDate);
                          } catch (e) {
                            return formatDateForDisplay(formData.declarationDate) || '-';
                          }
                        })() : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Executed NDA
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: formData.ndaDocument ? '#578A18' : '#ef4444' }}>
                        {formData.ndaDocument
                          ? (formData.ndaDocument instanceof File ? formData.ndaDocument.name : formData.ndaDocument)
                          : 'Not uploaded'}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Personal Information Processing Consent Accordion */}
              <Accordion
                expanded={expandedAccordion === 'consent'}
                onChange={(event, isExpanded) => {
                  setExpandedAccordion(isExpanded ? 'consent' : '');
                }}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  borderBottom: '1px solid #e5e7eb',
                  borderRadius: 0,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#6b7280' }} />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      my: 2
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                    Personal Information Processing Consent
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Full Name of Declarant
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.declarantFullName || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: '#6b7280', fontSize: '12px' }}>
                        Date
                      </Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                        {formData.declarationDate ? (() => {
                          try {
                            const date = parse(formData.declarationDate, 'yyyy-MM-dd', new Date());
                            if (!isNaN(date.getTime())) {
                              return format(date, 'do MMMM, yyyy');
                            }
                            return formatDateForDisplay(formData.declarationDate);
                          } catch (e) {
                            return formatDateForDisplay(formData.declarationDate) || '-';
                          }
                        })() : '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ backgroundColor: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column', pb: 0 }}>
      <Container maxWidth="lg" sx={{ pt: 3, pb: 4, flex: 1 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{
            color: '#6b7280',
            textTransform: 'none',
            fontSize: '14px',
            mb: 3,
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#374151'
            }
          }}
        >
          Home
        </Button>

        {/* What the reviewer sent it back for */}
        {infoRequest && (
          <Box
            sx={{
              mb: 3,
              p: { xs: 1.75, sm: 2.25 },
              border: '1px solid #fde68a',
              borderRadius: '8px',
              backgroundColor: '#fffbeb',
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#92400e', mb: 0.5 }}>
              {infoRequest.department
                ? `${infoRequest.department} needs more information`
                : 'More information needed'}
            </Typography>
            {infoRequest.comments && (
              <Typography sx={{ fontSize: '13px', color: '#b45309', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                {infoRequest.comments}
              </Typography>
            )}
            <Typography sx={{ fontSize: '13px', color: '#b45309', lineHeight: 1.6, mt: 1 }}>
              Update the application below, then submit it again from the review step.
            </Typography>
          </Box>
        )}

        {/* Custom Stepper */}
        <Box sx={{ mb: 4 }}>
          {/* Mobile View - Just Numbers */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', alignItems: 'center', mb: 3 }}>
            {steps.map((step, index) => (
              <Box key={step.number} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: index === activeStep ? theme.palette.green.main : 'transparent',
                    color: index === activeStep ? '#fff' : theme.palette.green.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '16px',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {index < activeStep ? <Check sx={{ fontSize: '16px', fontWeight: 'bold', color: theme.palette.green.main }} /> : step.number}
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      width: 40,
                      height: '1px',
                      backgroundColor: '#e5e7eb',
                      mx: 2
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>

          {/* Desktop View - Full Details */}
          <Grid
            container
            spacing={0}
            justifyContent="center"
            alignItems="flex-start"
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            {steps.map((step, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={step.number}
                sx={{
                  position: 'relative',
                  px: { xs: 2, md: 2 },
                  ...(index < steps.length - 1 && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      top: '80px',
                      height: '1px',
                      width: '100%',
                      maxWidth: '50px',
                      backgroundColor: '#e5e7eb',
                      display: { xs: 'none', md: 'block' }
                    }
                  })
                }}
              >
                <Box sx={{ textAlign: 'center', px: { xs: 1, md: 1 } }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: index === activeStep ? theme.palette.green.main : 'transparent',
                      border: 'none',
                      color: index === activeStep ? '#fff' : theme.palette.green.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 1.5,
                      fontWeight: 600,
                      fontSize: '20px',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    {index < activeStep ? <Check sx={{ fontSize: '20px', fontWeight: 'bold', strokeWidth: 2, color: theme.palette.green.main }} /> : step.number}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#000',
                      mb: 0.5
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      color: '#9ca3af',
                      fontWeight: 400,
                      lineHeight: 1.5
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Form Content */}
        <Box sx={{ mb: 3 }}>
          {renderStepContent(activeStep)}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          {/* Left side buttons */}
          <Box>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={handlePrevious}
                startIcon={<ArrowBack />}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  borderColor: '#d1d5db',
                  color: '#374151',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb'
                  }
                }}
              >
                Previous
              </Button>
            )}
          </Box>

          {/* Right side buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep < steps.length - 1 && (
              <Button
                variant="outlined"
                onClick={handleSaveDraft}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: '6px',
                  borderColor: '#d1d5db',
                  color: '#374151',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb'
                  }
                }}
              >
                Save Draft
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSaveAndContinue}
              disabled={loading}
              endIcon={activeStep === steps.length - 1 ? null : <ArrowForward />}
              sx={{
                backgroundColor: theme.palette.green.main,
                color: '#fff',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                px: 3,
                py: 1,
                borderRadius: '6px',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.green.hover,
                  boxShadow: 'none',
                },
              }}
            >
              {loading
                ? (activeStep === steps.length - 1 ? 'Submitting...' : 'Saving...')
                : (activeStep === steps.length - 1 ? 'Submit Application' : 'Save and Continue')}
            </Button>
          </Box>
        </Box>
      </Container>

      {/* File Viewer Modal */}
      <Dialog
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh', borderRadius: '8px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>{fileViewerName}</Typography>
          <IconButton onClick={handleCloseFileViewer} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#f5f5f5', minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {fileViewerUrl && (
            fileViewerName.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
              <img
                src={fileViewerUrl}
                alt={fileViewerName}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            ) : (
              <iframe
                src={fileViewerUrl}
                title={fileViewerName}
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileViewer}>Close</Button>
          <Button variant="contained" onClick={() => window.open(fileViewerUrl, '_blank')}>
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default SupplierApplication;
